import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable, observable } from 'mobx';
import { v4 } from 'uuid';
import { ACDLError, InputStatus } from '../../../models/error.model';
import { IMainApiService } from '../../../services/api.service';
import { IApiService } from '../../../services/api.service.base';
import previewImage from '../../../static/images/preview-min.jpg';
import { IAnnotationService } from '../../annotation/annotation.service';
import { IImage } from '../../annotation/annotations.interface';
import { IAnnotationsStore } from '../../annotation/annotations.store';
import { IAnnotationUiStore } from '../../annotation/annotationUi.store';
import { IFreeDrawSegmentationService } from '../../annotation/freeDrawSegmentation.service';
import { IQuestionModel } from '../../annotation/question.model';
import { IAnnotationTypeBl } from '../../annotation/submodules/annotationTypes/annotationType.bl';
import { IProjectDetailsToolsBl } from '../sub/tools/projectDetailsTools.bl';

export interface IProjectPreviewService {
  setupAsync(projectId: string): Promise<void>;
  dispose(): void;
}

export namespace IProjectPreviewService {
  export const $: interfaces.ServiceIdentifier<IProjectPreviewService> = Symbol('PROJECT_PREVIEW_SERVICE');
}

@injectable()
export class ProjectPreviewService implements IProjectPreviewService {
  constructor(
    @inject(IAnnotationService.$) private readonly annotationService: IAnnotationService,
    @inject(IAnnotationTypeBl.$) private readonly annotationTypeBl: IAnnotationTypeBl,
    @inject(IAnnotationUiStore.$) private readonly uiData: IAnnotationUiStore,
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(IFreeDrawSegmentationService.$) private readonly freeDrawSegmentationService: IFreeDrawSegmentationService,
    @inject(IProjectDetailsToolsBl.$) private readonly projectDetailsToolsBl: IProjectDetailsToolsBl,
    @inject(IAnnotationsStore.$) private readonly annotationsStore: IAnnotationsStore
  ) {
    makeObservable(this);
  }

  @action
  dispose(): void {
    this.annotationTypeBl.previewFinished();
    this.annotationsStore.questions.replace([]);
    this.annotationsStore.selectedSegmentation = undefined;
  }

  @action
  async setupAsync(projectId: string): Promise<void> {
    this.freeDrawSegmentationService.clear();
    this.annotationService.clearAnnotations();
    this.annotationsStore.image = undefined;
    this.uiData.changeIsImageLoading(true);

    await Promise.all([this.getRandomProjectImageAsync(projectId), this.projectDetailsToolsBl.getProjectDetailsAnnotationViewAsync(projectId)]);

    await this.annotationTypeBl.previewStarted(
      this.projectDetailsToolsBl.store.annotationTypes.map((m) => ({
        ...m,
        projectId,
        selectorType: m.selectorType,
        isUsed: false,
      }))
    );

    this.annotationsStore.questions.replace(
      this.projectDetailsToolsBl.store.questions.map((qd): IQuestionModel => {
        return {
          projectId: projectId,
          id: qd.id,
          type: qd.type,
          isRequired: qd.isRequired,
          text: qd.questionText,
          scopes: qd.scopes,
          answers: qd.answers.map((a) => ({
            id: a.id,
            text: a.text,
            selected: false,
          })),
          status: InputStatus.empty(),
        };
      })
    );
    this.annotationService.deselectSegmentation();
  }

  setDefaultImage(): void {
    this.annotationsStore.image = this.getDefaultImage();
  }

  getDefaultImage = (): IImage =>
    ({
      id: v4(),
      name: 'image-preview.jpg',
      height: 1641,
      width: 1094,
      url: previewImage,
      lowQualityUrl: previewImage,
      size: 0.429,
      dataSetName: 'Preview',
      datasetStatus: 'Published',
      isAssignedToCurrentUser: false,
      isAssigned: false,
      lockedByName: '',
      annotations: undefined,
      isWaitingForReviewAfterReject: false,
    }) as IImage;

  async getRandomProjectImageAsync(projectId: string): Promise<void> {
    const result = await this.apiService.getAsync<IImage | ACDLError>(`/Images/GetProjectDraftRandomImage/${projectId}`);

    if (!result || result instanceof ACDLError) {
      this.setDefaultImage();
    } else {
      this.annotationsStore.image = observable({ ...result, url: '', lowQualityUrl: '' });
      await this.startLoading(this.annotationsStore.image);
    }
  }

  async startLoading(item: IImage): Promise<void> {
    await Promise.all([this.loadLowQualityImage(item), this.loadImage(item)]);
  }

  async loadLowQualityImage(image: IImage): Promise<void> {
    const imageFile = await this.apiService.getImageAsync(this.apiService.getUrl(`/Images/GetImageThumbnail/${image.id}`));
    if (!(imageFile instanceof ACDLError)) {
      image.lowQualityUrl = URL.createObjectURL(imageFile.blob);
    }
  }

  async loadImage(image: IImage): Promise<void> {
    const imageFile = await this.apiService.getImageAsync(this.apiService.getUrl(`/Images/GetImage/${image.id}`));
    if (!(imageFile instanceof ACDLError)) {
      image.url = URL.createObjectURL(imageFile.blob);
    }
  }
}
